import type { ArtHouseId, ArtRoomDefinition } from "./types";

export const VAN_GOGH_ROOM: ArtRoomDefinition = {
  id: "van-gogh-house",
  title: "Van Gogh House",
  livePainting: true,
  liveBrushes: ["firefly", "growth", "color-liquify-splash"],
  palette: [
    "#102451", "#173b78", "#285d9b", "#457da4",
    "#d18b25", "#e8ad32", "#f3cf62", "#eadca6",
    "#6d3f28", "#3f6848", "#66864c", "#b95b35",
  ],
  artTip: "Borrow the room's direction, edge and value instead of filling the whole body one color.",
  defaultShape: "streak",
  starterCamo: {
    label: "Van Gogh Camo",
    help: "A removable cobalt, ochre and sunflower-colored starting layer.",
    style: "swirl",
    baseColors: [0, 1, 2, 3],
    accentColors: [4, 5, 6, 8, 9, 10, 11],
  },
};

export const MONET_ROOM: ArtRoomDefinition = {
  id: "monet-garden-house",
  title: "Monet Garden House",
  palette: [
    "#435f68", "#5f7f7b", "#7fa39a", "#a9c0ad",
    "#6d73a5", "#8f8fbd", "#b8acd0", "#d9cae0",
    "#c99b83", "#e1b9a3", "#d8c873", "#eee2b8",
  ],
  artTip: "Match light and temperature before hue. Neighboring translucent dabs mix at room scale.",
  defaultShape: "dot",
  starterCamo: {
    label: "Garden Light Camo",
    help: "A removable layer of teal, lavender, rose and pale-yellow reflected light.",
    style: "optical",
    baseColors: [0, 1, 2, 4, 5, 6],
    accentColors: [3, 7, 8, 9, 10, 11],
  },
};

export const OUTDOOR_MASTERS_ROOM: ArtRoomDefinition = {
  id: "outdoor-masters-journey",
  title: "Outdoor Masters Journey",
  palette: [
    "#102c67", "#174ca6", "#e4572e", "#f28c28",
    "#ffc93c", "#efe06a", "#1f7a62", "#65a844",
    "#8b3fa0", "#d94f8a", "#f2b05e", "#5b274f",
  ],
  artTip: "Match the big light and dark shapes first, then let bold complementary color carry the camouflage.",
  defaultShape: "streak",
  starterCamo: {
    label: "Wild Outdoor Camo",
    help: "A removable layer of cobalt, orange, green and violet marks that follows the terrace and garden paths.",
    style: "swirl",
    baseColors: [0, 1, 2, 3, 4, 11],
    accentColors: [5, 6, 7, 8, 9, 10],
  },
};

export const COLOR_REBIRTH_ROOM: ArtRoomDefinition = {
  id: "world-remembers-color",
  title: "Color Rebirth",
  palette: [
    "#17191a", "#343a3b", "#5d6769", "#899294",
    "#b8b7ad", "#e4ddcb", "#f4efe1", "#6f8492",
    "#a94838", "#c9634b", "#d8b46a", "#f2dba4",
  ],
  artTip: "Start with value and soft edges. Let a tiny amount of color do the surprising work.",
  defaultShape: "dot",
  starterCamo: {
    label: "World Rewrite Camo",
    help: "Paper white, diluted blue-gray, charcoal and one small vermilion trace match the storm's structure.",
    style: "wash",
    baseColors: [0, 1, 2, 3, 4, 5, 6, 7],
    accentColors: [8, 9, 10, 11],
  },
};

export const LUMINOUS_TIDE_ROOM: ArtRoomDefinition = {
  id: "luminous-tide-dreamscape",
  title: "The Tide Dreams in Starlight",
  palette: [
    "#07132f", "#102b58", "#174a83", "#147fa5",
    "#25b7c6", "#79e2dc", "#473b88", "#7658b2",
    "#aa79cf", "#d89bc8", "#c69a45", "#f0d88a",
  ],
  artTip: "Match the dark-to-glow boundary first. A tiny cyan edge inside a big navy shape can disappear beautifully.",
  defaultShape: "dot",
  starterCamo: {
    label: "Moon-Tide Camo",
    help: "A removable layer of midnight stone, cyan tide-light, violet flowers and small gold reflections.",
    style: "optical",
    baseColors: [0, 1, 2, 3, 6, 7],
    accentColors: [4, 5, 8, 9, 10, 11],
  },
};

export const UNFINISHED_MORNING_ROOM: ArtRoomDefinition = {
  id: "unfinished-morning",
  title: "The Unfinished Morning",
  livePainting: true,
  liveBrushes: ["blue-current", "liquid-color", "graphite-whisper"],
  palette: [
    "#f4ead7", "#dfd1ba", "#9b9387", "#555752",
    "#9db8c7", "#bad0c4", "#d6a58f", "#bc654d",
    "#b9914e", "#466a86", "#77836a", "#b34f3f",
  ],
  artTip: "Treat blank paper as an active color: match the edge between finished pigment, erased construction and untouched canvas.",
  defaultShape: "streak",
  starterCamo: {
    label: "Unfinished Morning Camo",
    help: "Ivory paper, graphite structure and pale colored shadows leave deliberate incomplete gaps.",
    style: "wash",
    baseColors: [0, 1, 2, 3, 4, 5],
    accentColors: [6, 7, 8, 9, 10, 11],
  },
};

// The Live Gallery joins every house into one strip, so its palette spans them
// all. Hidden from the lobby and reached by URL; the owner tunes it over time.
export const LIVE_GALLERY_ROOM: ArtRoomDefinition = {
  id: "live-gallery",
  title: "Live Gallery",
  palette: [
    "#102451", "#285d9b", "#7fa39a", "#25b7c6",
    "#e8ad32", "#f3cf62", "#e4572e", "#f28c28",
    "#66864c", "#8b3fa0", "#d94f8a", "#f4efe1",
  ],
  artTip: "Every room here belongs to a different painter — match the room you're hiding in, not one fixed style.",
  defaultShape: "streak",
  starterCamo: {
    label: "Gallery Camo",
    help: "A removable all-house layer of cobalt, tide-cyan, ochre, orange, green and violet marks.",
    style: "wash",
    baseColors: [0, 1, 2, 3, 11],
    accentColors: [4, 5, 6, 7, 8, 9, 10],
  },
};

export const ART_ROOMS: Readonly<Record<ArtHouseId, ArtRoomDefinition>> = {
  "van-gogh-house": VAN_GOGH_ROOM,
  "monet-garden-house": MONET_ROOM,
  "outdoor-masters-journey": OUTDOOR_MASTERS_ROOM,
  "world-remembers-color": COLOR_REBIRTH_ROOM,
  "luminous-tide-dreamscape": LUMINOUS_TIDE_ROOM,
  "unfinished-morning": UNFINISHED_MORNING_ROOM,
  "live-gallery": LIVE_GALLERY_ROOM,
};

export function artRoomFor(id: ArtHouseId): ArtRoomDefinition {
  return ART_ROOMS[id];
}
